import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Flex, Text } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { AddressRes } from '@dealstack/shared/lib/generated';
import { OverflowBadge } from '@dealstack/frontend/app/pages/Valuations/components/OverflowBadgeList/OverflowBadge.tsx';
import { EditableTileContainer } from '@dealstack/frontend/shared/components/EditableTileContainer/EditableTileContainer.tsx';
import { TEST_ELEMENT_IDS, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds';

interface Props {
	address?: AddressRes;
	hasEditorPermission?: boolean;
	onEditClick?: () => void;
	onDeleteClick?: () => void;
}

export const AddressTile = ({
	address,
	hasEditorPermission,
	onEditClick,
	onDeleteClick,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const [formattedAddress, setFormattedAddress] = useState<string>();

	useEffect(() => {
		if (!address) {
			setFormattedAddress('');
			return;
		}

		const { houseNumber, street, city, country, postCode } = address;
		const addressString = [houseNumber, street, city, postCode, country]
			.filter((item) => item?.trim())
			.join(', ');

		setFormattedAddress(addressString);
	}, [address]);

	return (
		<EditableTileContainer
			hasEditPermission={hasEditorPermission}
			onEditClick={onEditClick}
			onDeleteClick={onDeleteClick}
			dataTestId={makeComplexDataTestId(TEST_ELEMENT_IDS.CAP_ADDRESS_TILE, address?.postCode)}
			deleteBtnDataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.EDITABLE_TILE_DELETE_BUTTON,
				address?.postCode
			)}
			editBtnDataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.EDITABLE_TILE_EDIT_BUTTON,
				address?.postCode
			)}
		>
			{formattedAddress ? (
				<Flex direction="column">
					<Text fw={600} c="dark.8">
						{formattedAddress}
					</Text>

					{!!address?.tags?.length && (
						<Flex gap="0.5rem" mt="0.25rem" wrap="wrap">
							{address.tags.map((tag) => (
								<OverflowBadge
									key={tag}
									badge={{
										id: tag,
										label: tCapManage(`basic_details.address_form.tags.${tag}`),
									}}
								/>
							))}
						</Flex>
					)}
				</Flex>
			) : (
				<Text size="B6Medium">{tCapManage('basic_details.address_form.noData')}</Text>
			)}
		</EditableTileContainer>
	);
};
